/**
 * TermsContent — the ONE Terms of Service body (2026-08-23).
 *
 * Extracted from src/pages/Terms.tsx so the same legal text renders on the
 * /terms-of-service page AND inside the onboarding confirm card's in-flow
 * popup (owner directive: agreeing to the terms must never navigate the user
 * out of onboarding; the popup shows the full, selectable, copy-pastable
 * text). Edit the terms HERE — both surfaces render this component.
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { brandColors, lightTheme } from '../../styles/theme';

const fadeInLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const SectionTitle = styled.h2`
  font-family: ${lightTheme.fonts.heading};
  font-size: 1.8rem;
  color: ${brandColors.primaryBlack};
  margin-bottom: 1.5rem;
  margin-top: 3rem;
  animation: ${fadeInLeft} 0.6s ease-out;
  animation-delay: 0.2s;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${brandColors.border};
`;

const SubSectionTitle = styled.h3`
  font-family: ${lightTheme.fonts.heading};
  font-size: 1.4rem;
  color: ${brandColors.secondaryDark};
  margin-bottom: 1rem;
  margin-top: 2rem;
`;

const TermsText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${brandColors.textSecondary};
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const TermsList = styled.ul`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
`;

const TermsListItem = styled.li`
  font-size: 1rem;
  line-height: 1.6;
  color: ${brandColors.textSecondary};
  margin-bottom: 0.7rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const InlineLink = styled(Link)`
  color: ${brandColors.accentGold};
  text-decoration: none;
  font-weight: 500;
  border-bottom: 1px solid rgba(198, 167, 94, 0.3);
  transition: color 0.2s ease, border-bottom-color 0.2s ease;

  &:hover {
    color: ${brandColors.hoverGold};
    border-bottom-color: ${brandColors.hoverGold};
  }
`;

const KeyHighlightsGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  margin: 2.5rem 0 3.5rem;
`;

const KeyHighlightCard = styled.div`
  background: ${brandColors.backgroundSoftWhite};
  border-radius: 16px;
  padding: 1.75rem;
  box-shadow: ${lightTheme.shadows.lg};
  border: 1px solid ${brandColors.border};
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${lightTheme.shadows.xl};
    border-color: ${brandColors.accentGold};
  }
`;

const KeyHighlightIcon = styled.span`
  font-size: 1.6rem;
`;

const KeyHighlightTitle = styled.h3`
  font-family: ${lightTheme.fonts.heading};
  font-size: 1.1rem;
  font-weight: 600;
  color: ${brandColors.primaryBlack};
  text-transform: lowercase;
  margin: 0;
`;

const KeyHighlightText = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${brandColors.textSecondary};
  margin: 0;
`;

const TermsOrderedList = styled.ol`
  margin-bottom: 1.5rem;
  padding-left: 1.5rem;
  counter-reset: item;

  li {
    font-size: 1rem;
    line-height: 1.6;
    color: ${brandColors.textSecondary};
    margin-bottom: 0.7rem;

    @media (max-width: 768px) {
      font-size: 0.95rem;
    }
  }
`;

export const TermsContent: React.FC = () => (
  <>
      <TermsText>
        Welcome to prest0. These terms of service ("terms") explain the rules that apply when you interact with our website, platform, and AI agent tooling. Please read everything carefully before you create an account or continue using the platform.
      </TermsText>
      <TermsText>
        By accessing the platform or creating an account you agree to these terms, our{' '}
        <InlineLink to="/privacy-policy">privacy policy</InlineLink>, and our{' '}
        <InlineLink to="/cookie-policy">cookie policy</InlineLink>. If you disagree with any part of the terms, you must discontinue use of the platform immediately.
      </TermsText>
      <TermsText>
        The operational safeguards referenced throughout these terms mirror the controls described on the security page—TLS 1.3 and HSTS at the edge, AES-256 encryption on Google Cloud, enterprise OAuth 2.0 with MFA, a contractual no-training guarantee, documented retention schedules with deletion on request, append-only audit logs, and SOC 2 TYPE II / ISO/IEC 27001 controls inherited from Google Cloud.
      </TermsText>

      <KeyHighlightsGrid>
        <KeyHighlightCard>
          <KeyHighlightIcon>🤝</KeyHighlightIcon>
          <KeyHighlightTitle>clear accountability</KeyHighlightTitle>
          <KeyHighlightText>
            You control the AI agents you build. Keep their data lawful, respect third-party rights, and stay transparent with anyone who interacts with them.
          </KeyHighlightText>
        </KeyHighlightCard>
        <KeyHighlightCard>
        <KeyHighlightIcon>🔐</KeyHighlightIcon>
        <KeyHighlightTitle>verified security</KeyHighlightTitle>
        <KeyHighlightText>
          TLS 1.3 ingress, AES-256 on Google Cloud, enterprise OAuth 2.0 with MFA, and inherited SOC 2 TYPE II / ISO 27001 controls apply to every workspace.
        </KeyHighlightText>
        </KeyHighlightCard>
        <KeyHighlightCard>
          <KeyHighlightIcon>🚀</KeyHighlightIcon>
        <KeyHighlightTitle>no-training guarantee</KeyHighlightTitle>
        <KeyHighlightText>
          Prompts, transcripts, and attachments never train shared models—enterprise AI endpoints and deletion on request keep custody in your hands.
        </KeyHighlightText>
        </KeyHighlightCard>
      </KeyHighlightsGrid>

      <SectionTitle id="acceptance">acceptance of terms</SectionTitle>
      <TermsText>
        These terms constitute a binding agreement between you and prest0. If you are using the platform on behalf of an organization, you represent that you have authority to bind that organization and the term "you" extends to that entity.
      </TermsText>
      
      <SectionTitle id="eligibility">eligibility</SectionTitle>
      <TermsText>
        You must be at least 18 years old and legally capable of entering into contracts to create an account. You may not use the platform if you are barred from doing so under any applicable law or if export-control rules prohibit access from your jurisdiction.
      </TermsText>

      <SectionTitle id="accounts">account creation and security</SectionTitle>
      <TermsText>
        When you create an account you agree to:
      </TermsText>
      <TermsList>
        <TermsListItem>provide accurate, current, and complete registration information;</TermsListItem>
        <TermsListItem>maintain the security of your credentials and promptly update them if compromised;</TermsListItem>
        <TermsListItem>limit access to your account to yourself or authorized team members; and</TermsListItem>
        <TermsListItem>enforce multi-factor authentication when prompted by our OAuth 2.0 flows and device posture checks; and</TermsListItem>
        <TermsListItem>notify us immediately if you suspect unauthorized access or other security incidents.</TermsListItem>
      </TermsList>
      <TermsText>
        You are responsible for all activity that occurs under your account, including actions taken by invited collaborators. We operate a 48-hour incident notification SLA (as described on the security page) and will issue forensic summaries if a breach affects your workspace.
      </TermsText>

      <SectionTitle id="platform-use">platform access and updates</SectionTitle>
      <TermsText>
        We continually improve the platform. Features may change, be discontinued, or move to different plans without notice. We strive to provide advance communication for impactful changes and will use commercially reasonable efforts to avoid service interruptions during business-critical hours.
      </TermsText>

      <SectionTitle id="acceptable-use">acceptable use requirements</SectionTitle>
      <TermsText>
        You agree not to misuse the platform. Prohibited behavior includes, but is not limited to:
      </TermsText>
      <TermsList>
        <TermsListItem>violating any applicable law, regulation, or industry guideline;</TermsListItem>
        <TermsListItem>impersonating any person or entity or misrepresenting your affiliation with prest0;</TermsListItem>
        <TermsListItem>probing, scanning, or testing the vulnerability of the platform without prior written consent;</TermsListItem>
        <TermsListItem>introducing malware, bots, or other harmful code;</TermsListItem>
        <TermsListItem>overloading or disrupting the infrastructure supporting the platform;</TermsListItem>
        <TermsListItem>harvesting or scraping information, including personal data, without prior authorization; and</TermsListItem>
        <TermsListItem>using the platform to generate or disseminate content that is deceptive, exploitative, or likely to cause harm.</TermsListItem>
      </TermsList>
      
      <SectionTitle id="security">security standards</SectionTitle>
      <TermsText>
        The technical safeguards described on our security page apply to every customer workspace. By using the platform you acknowledge and agree to the following baseline controls:
      </TermsText>
      <TermsList>
        <TermsListItem>TLS 1.3 with HSTS protects all browser and API sessions, and legacy protocols are disabled.</TermsListItem>
        <TermsListItem>Data stored in Google Cloud Firestore and Storage is encrypted with AES-256 and managed key rotation.</TermsListItem>
        <TermsListItem>Enterprise OAuth 2.0, hardware-backed MFA, device posture checks, and scoped API keys enforce least privilege.</TermsListItem>
        <TermsListItem>A contractual no-training guarantee keeps prompts, transcripts, and attachments out of shared model training pipelines.</TermsListItem>
        <TermsListItem>We operate documented retention schedules and delete your data on request across storage and transcripts. Self-service retention timers and automated deletion receipts are on the roadmap, not shipped.</TermsListItem>
        <TermsListItem>We inherit SOC 2 TYPE II and ISO/IEC 27001 controls from Google Cloud. Prest0 has not completed an audit or a penetration test of its own; both are planned. We maintain append-only audit logs.</TermsListItem>
      </TermsList>
      <TermsText>
        You must implement complementary safeguards (for example, strong identity management and data classification) so that your use of the platform aligns with these standards.
      </TermsText>
      
      <SectionTitle id="ai-agents">ai agent responsibilities</SectionTitle>
      <TermsText>
        Our tooling empowers you to design AI agents, workflows, and automations. You alone are responsible for the outputs, decisions, disclosures, and interactions of those AI agents. You agree to:
      </TermsText>
      <TermsList>
        <TermsListItem>ensure that AI agents clearly communicate they are automated experiences when applicable;</TermsListItem>
        <TermsListItem>avoid training or deploying agents that discriminate, infringe intellectual property, or violate privacy;</TermsListItem>
        <TermsListItem>maintain accurate documentation of prompts, guardrails, and data sources used by each agent;</TermsListItem>
        <TermsListItem>respect third-party API, model, and dataset license terms that may govern the resources you connect;</TermsListItem>
        <TermsListItem>implement human oversight for decisions that could impact finances, safety, or legal obligations; and</TermsListItem>
        <TermsListItem>keep agent prompts within the zero-training guardrails (no attempts to bypass redaction, retention timers, or logged execution paths); and</TermsListItem>
        <TermsListItem>comply with any additional requirements we publish for regulated or high-risk use cases.</TermsListItem>
      </TermsList>
      
      <SectionTitle id="payments">plans, billing, and monetization</SectionTitle>
      <TermsText>
        Some features may require paid plans or usage-based fees. Unless otherwise stated in a separate order form, all fees are due immediately, non-refundable, and exclusive of applicable taxes. If we enable monetization of your AI agents, supplemental terms covering payouts, revenue shares, and compliance with tax reporting obligations will apply and must be accepted before revenue is disbursed.
      </TermsText>
      
      <SectionTitle id="intellectual-property">intellectual property</SectionTitle>
      <TermsText>
        prest0 retains all rights, title, and interest in the platform, its look and feel, and all related intellectual property. You retain ownership of the content, prompts, workflows, and AI configurations you create. By submitting or publishing content through the platform you grant prest0 a worldwide, royalty-free license to host, display, and use that content solely to operate, maintain, and improve the platform.
      </TermsText>
      
      <SectionTitle id="confidentiality">confidentiality and security</SectionTitle>
      <TermsText>
        Both parties agree to protect any confidential information received from the other party with the same degree of care used to safeguard their own confidential data. You may only use confidential information for purposes of evaluating or using the platform. We will continue to operate the layered controls referenced above (TLS 1.3, AES-256 on Google Cloud, enterprise OAuth 2.0 with MFA, immutable audit logs, and planned penetration tests). You are responsible for encrypting or anonymizing any sensitive data you choose to import and for complying with all applicable breach-notification laws.
      </TermsText>
      
      <SectionTitle id="privacy">privacy and data protection</SectionTitle>
      <TermsText>
        We process personal data in accordance with our{' '}
        <InlineLink to="/privacy-policy">privacy policy</InlineLink> and the security commitments summarized on the security page. Those commitments include the zero-training guarantee, documented retention schedules with deletion on request, SOC 2 TYPE II / ISO 27001 controls inherited from Google Cloud, and our breach-notification commitment.
      </TermsText>
      <TermsText>
        You represent that you have obtained all necessary consents and have a lawful basis for any personal data you share with us through the platform. If you connect external data sources, you remain responsible for honoring your own retention and privacy obligations.
      </TermsText>
      
      <SectionTitle id="sms-communications">sms communications</SectionTitle>
      {/* PRE-72: /signup checkbox links to /terms-of-service#sms. Expose a
          short 'sms' anchor inside this section so the link scrolls correctly
          without renaming the existing id (which is used elsewhere). */}
      <span id="sms" style={{ display: 'block', position: 'relative', top: '-120px' }} aria-hidden="true" />
      <TermsText>
        prest0 may send SMS text messages to your mobile phone number as part of the services, including account notifications, case updates, and appointment reminders. By providing your phone number and opting in to SMS notifications, you agree to the following:
      </TermsText>
      <TermsList>
        <TermsListItem>you agree to receive text messages from prest0 related to your account and services at the phone number you provide.</TermsListItem>
        <TermsListItem>standard message and data rates from your carrier may apply to all messages sent and received.</TermsListItem>
        <TermsListItem>you may opt out at any time by texting STOP in reply to any message from prest0. You will receive one final confirmation and then no further texts.</TermsListItem>
        <TermsListItem>prest0 is not responsible for delayed or undelivered messages due to carrier issues, network outages, or factors outside our control.</TermsListItem>
        <TermsListItem>you represent that the phone number you provide is assigned to you and that you are authorized to receive messages at that number. You agree to notify prest0 promptly if you change or reassign your phone number.</TermsListItem>
        <TermsListItem>consent to receive SMS messages is not a condition of purchase or use of any prest0 service.</TermsListItem>
        <TermsListItem>for help with SMS communications, reply HELP to any message or contact <a href="mailto:support@prest0.ai" style={{ color: 'inherit', textDecoration: 'underline' }}>support@prest0.ai</a>.</TermsListItem>
      </TermsList>
      {/* PRE-72: additive carrier-compliance-grade consent language. */}
      <TermsText>
        Consent to the SMS program is captured via an explicit, unchecked-by-default
        checkbox at the point of signup or in your account settings. Your agreement
        to these terms includes the following acknowledgements:
      </TermsText>
      <TermsList>
        <TermsListItem>the SMS consent checkbox is independent of the Terms of Service / Privacy Policy acknowledgement; ticking one does not imply the other.</TermsListItem>
        <TermsListItem>standard carrier message and data rates apply to every inbound and outbound message.</TermsListItem>
        <TermsListItem>you may opt out at any time by replying STOP; your right to opt out is absolute and cannot be waived.</TermsListItem>
        <TermsListItem>prest0 may cease sending you SMS at any time, with or without notice, for operational, legal, or abuse-prevention reasons.</TermsListItem>
        <TermsListItem>prest0 maintains an auditable consent recordkeeping system — timestamp, IP, user-agent, and opt-in/opt-out events — for as long as required under A2P 10DLC, TCPA, and similar carrier-compliance regulations.</TermsListItem>
        <TermsListItem>for the full SMS program description, including frequency, message categories, and support contact, see the dedicated <InlineLink to="/sms-terms">SMS Terms</InlineLink> page.</TermsListItem>
      </TermsList>

      <SectionTitle id="third-parties">third-party services</SectionTitle>
      <TermsText>
        The platform may connect to third-party APIs, models, datasets, or integrations. Those services are governed by their own terms. We are not responsible for the availability or performance of third-party offerings and may disable integrations that become unstable, insecure, or non-compliant.
      </TermsText>
      
      <SectionTitle id="beta">beta features and feedback</SectionTitle>
      <TermsText>
        From time to time we may invite you to test early or experimental features. Beta features are provided as-is, may be discontinued at any time, and are confidential unless we state otherwise. By providing feedback you grant prest0 the right to use and incorporate that feedback without restriction or obligation to you.
      </TermsText>
      
      <SectionTitle id="warranty">disclaimer of warranties</SectionTitle>
      <TermsText>
        The platform is provided on an "as is" and "as available" basis. prest0 expressly disclaims any warranties of merchantability, fitness for a particular purpose, title, and non-infringement. We do not guarantee uninterrupted, error-free, or completely secure operation, nor do we warrant that AI outputs will meet your expectations or comply with your intended use.
      </TermsText>
      
      <SectionTitle id="liability">limitation of liability</SectionTitle>
      <TermsText>
        To the maximum extent permitted by law, neither prest0 nor its affiliates, suppliers, or licensors will be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages, or for any loss of profits, revenues, goodwill, data, or business opportunities arising from or related to your use of the platform, even if we have been advised of the possibility of such damages.
      </TermsText>
      <TermsText>
        Our aggregate liability for all claims related to the platform will not exceed the greater of (a) the amounts you paid us for the service in the twelve months preceding the event giving rise to the claim, or (b) one hundred U.S. dollars ($100).
      </TermsText>
      
      <SectionTitle id="indemnification">indemnification</SectionTitle>
      <TermsText>
        You agree to defend, indemnify, and hold harmless prest0 and our officers, directors, employees, and agents from and against any claims, damages, obligations, losses, liabilities, costs, or expenses (including reasonable attorney fees) arising from your use of the platform, your AI agents, your violation of these terms, or your infringement of any third-party rights.
      </TermsText>
      
      <SectionTitle id="suspension">suspension and termination</SectionTitle>
      <TermsText>
        We may suspend or terminate access to the platform immediately and without notice if we believe you have violated these terms, created risk or possible legal exposure for prest0, or if we are required to do so by law. Upon termination you must cease all use of the platform and delete any confidential information obtained through it.
      </TermsText>
      
      <SectionTitle id="compliance">compliance with laws</SectionTitle>
      <TermsText>
        You will comply with all applicable export control and sanctions laws, anti-corruption laws, and data protection regulations. You agree not to use the platform for unlawful surveillance, discrimination, or to create content that violates intellectual property, privacy, or publicity rights.
      </TermsText>
      
      <SectionTitle id="disputes">dispute resolution</SectionTitle>
      <TermsText>
        We prefer to resolve issues informally. Before filing a formal claim, please contact us so we can attempt to resolve the matter together. If we cannot resolve a dispute within thirty (30) days, either party may pursue remedies available under applicable law.
      </TermsText>
      <TermsOrderedList>
        <li>send us written notice of the dispute that describes the issue and the relief sought;</li>
        <li>work with us in good faith to resolve the dispute informally; and</li>
        <li>if unresolved after 30 days, pursue mediation, arbitration, or litigation in accordance with governing law.</li>
      </TermsOrderedList>
      
      <SectionTitle id="governing-law">governing law and venue</SectionTitle>
      <TermsText>
        These terms are governed by the laws specified in your order form or, if none is specified, the laws that apply to prest0’s corporate domicile, without regard to conflict-of-law principles. Any disputes arising under these terms will be resolved exclusively in the competent courts associated with that governing law, and both parties consent to the jurisdiction of those courts.
      </TermsText>
      
      <SectionTitle id="changes">changes to these terms</SectionTitle>
      <TermsText>
        We may update these terms from time to time. Material changes will be announced via email, in-product notifications, or prominent notices on the platform at least thirty (30) days before they become effective whenever practicable. Your continued use of the platform after updated terms take effect constitutes acceptance of the changes.
      </TermsText>
  </>
);

export default TermsContent;
